import { PharmaStpFileModel } from "../../data-model/nycbsPharmaOrder";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logDataToConsole, logError } from "../../utils/customLogger";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { getBearerToken } from "../postCall/bearerToken";

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const token = await getBearerToken();
  return {
    Authorization: `Bearer ${token}`,
  };
};

const createRequestOptions = (
  body?: Record<string, unknown>,
  header?: Record<string, string>,
  requestType: "POST" | "GET" = "POST",
): RequestInit => {
  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  if (header) {
    for (const key in header) {
      headers.append(key, header[key]);
    }
  }

  const options: RequestInit = {
    method: requestType,
    headers,
    redirect: "follow",
  };

  if (requestType === "POST" && body) {
    options.body = JSON.stringify(body);
  }

  return options;
};

const handleApiResponse = async (response: Response): Promise<any> => {
  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
  return await response.json();
};

const fetchApiData = async (
  url: string,
  logKey: string,
  body?: Record<string, unknown>,
  headers?: Record<string, string>,
  requestType: "POST" | "GET" = "POST",
): Promise<any> => {
  const requestOptions = createRequestOptions(body, headers ?? {}, requestType);

  try {
    const response = await fetch(url, requestOptions);
    const result = await handleApiResponse(response);
    logDataToConsole(logKey, result);
    return result;
  } catch (error) {
    logError(error as Error, `Error in ${logKey}`);
    throw error;
  }
};

export interface OrderByItem {
  column: string;
  direction: "ASC" | "DESC";
}

export interface FetchOncologySubmissionDataParams {
  filters?: {
    seq?: string[];
    dumped_at?: string[];
    [key: string]: any;
  };
  /** Set to false so structured search predicates match case-insensitively. */
  case_sensitive?: boolean;
  page?: number;
  page_size?: number;
  request_id?: string;
  order_by?: OrderByItem[];
  /** When set, overrides the default org resolved from local storage. */
  org_id?: string;
}

export interface OncologySubmissionApiResponse {
  success: boolean;
  message: string;
  rows: OncologySubmissionApiRow[];
  row_count: number;
  page: number;
  page_size: number;
  total_pages: number;
  total_count: number;
}

/** Oncology submission API row; same shape as pharma STP file orders. */
export type OncologySubmissionApiRow = PharmaStpFileModel;

/**
 * Fetch oncology submission data using dumped_at filter in filters object
 * This API does not use date_from_filename_start/end, instead uses filters.dumped_at
 */
export const fetchOncologySubmissionData = async (
  params: FetchOncologySubmissionDataParams,
): Promise<OncologySubmissionApiResponse> => {
  const orgId = params.org_id ?? getOrgIdForFetchExternalWorklist();
  const body: Record<string, unknown> = {
    org_id: orgId ?? "",
    filters: params.filters ?? {},
    page: params.page ?? 1,
    request_id: params.request_id,
    order_by: params.order_by,
  };

  // Only include page_size if it's explicitly provided
  if (params.page_size !== undefined) {
    body.page_size = params.page_size;
  }

  if (params.case_sensitive !== undefined) {
    body.case_sensitive = params.case_sensitive;
  }

  const headers = await getAuthHeaders();

  return await fetchApiData(
    API_ENDPOINTS.GET_PHARMA_STP_FILE_DATA,
    "fetchOncologySubmissionData",
    body,
    headers,
    "POST",
  );
};
